import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth, homeFor } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Volleyball, MailOpen } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('email');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState(null);
  const [noAccount, setNoAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNoAccount(null);
    try {
      const { data } = await api.post('/auth/request-otp', { email });
      if (data.dev_code) setDevCode(data.dev_code);
      setStep('otp');
      toast.success(t('login.codeSent'));
    } catch (err) {
      if (err.response?.status === 404) {
        setNoAccount(err.response?.data?.detail === 'pending_consent' ? 'pending_consent' : 'no_account');
      } else {
        toast.error(err.response?.data?.detail || t('login.sendError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', { email, code });
      login(data.token, data.user);
      navigate(homeFor(data.user.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('login.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-nuit-900 flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 max-w-md w-full gold-glow"
        data-testid="login-page">
        <div className="flex items-center gap-2 font-fredoka font-semibold text-lg text-or-400 mb-8">
          <span className="w-9 h-9 rounded-full bg-or-400 flex items-center justify-center">
            <Volleyball className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
          </span>
          {t('login.title')}
        </div>
        {step === 'email' ? (
          <form onSubmit={requestCode} className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-creme mb-2" htmlFor="login-email">{t('login.email')}</label>
              <Input id="login-email" data-testid="login-email-input" required type="email" value={email}
                onChange={(e) => { setEmail(e.target.value); setNoAccount(null); }} placeholder={t('login.emailPlaceholder')}
                className="h-14 rounded-2xl text-base bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
            </div>
            <p className="text-xs text-brume">{t('login.noPassword')}</p>
            <Button type="submit" data-testid="login-request-code-button" disabled={loading}
              className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform">
              {loading ? t('common.sending') : t('login.requestCode')}
            </Button>
            {noAccount && (
              <div data-testid="login-no-account" className="bg-nuit-900 border border-or-400/40 rounded-2xl p-4 space-y-3">
                <p className="text-sm text-creme leading-relaxed">
                  {noAccount === 'pending_consent' ? t('login.pendingConsent') : t('login.noAccount')}
                </p>
                {noAccount === 'no_account' && (
                  <Link to="/demande-acces" data-testid="login-request-access-link"
                    className="inline-block text-sm text-or-400 font-bold hover:underline">
                    {t('login.noAccountCta')}
                  </Link>
                )}
              </div>
            )}
            <p className="text-center text-sm text-brume">
              <Link to="/" className="text-or-400 font-bold hover:underline" data-testid="login-back-link">
                {t('common.backHome')}
              </Link>
            </p>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-6">
            <div className="flex items-start gap-3 bg-nuit-900 border border-nuit-600 rounded-2xl p-4">
              <MailOpen className="w-5 h-5 text-or-400 mt-0.5 shrink-0" strokeWidth={2.5} />
              <p className="text-sm text-brume leading-relaxed">
                {t('login.codeSentTo', { email })}
              </p>
            </div>
            {devCode && (
              <p className="text-center text-xs font-bold text-amber-800 bg-amber-200 rounded-full px-4 py-2"
                data-testid="dev-code-hint">
                {t('login.devMode', { code: devCode })}
              </p>
            )}
            <div className="flex justify-center" data-testid="otp-input-wrapper">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="login-otp-input">
                <InputOTPGroup className="gap-2">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="w-11 h-14 rounded-xl border-2 border-nuit-600 bg-nuit-900 text-creme text-xl font-bold" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button type="submit" data-testid="login-verify-button" disabled={loading || code.length < 6}
              className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform">
              {loading ? t('landing.checking') : t('login.submit')}
            </Button>
            <button type="button" onClick={() => { setStep('email'); setCode(''); setDevCode(null); }}
              data-testid="login-change-email-button"
              className="w-full text-center text-sm text-or-400 font-bold hover:underline">
              {t('login.changeEmail')}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
